import React from "react";

const QUICK_MOQ = [100, 200, 250, 500, 1000, 3000];

const PriceAndQuantity = ({ varietyMOQ, priceAndQuantity, onChange, floorPrice,
  capPrice, standardizedUnit }) => {
  const { MOQ, currency, expectedPriceRange } = priceAndQuantity;

  const totalMin = Number(MOQ) > 0 && Number(expectedPriceRange?.min) > 0
    ? (Number(MOQ) * Number(expectedPriceRange.min)).toLocaleString("en-IN")
    : null;
  const totalMax = Number(MOQ) > 0 && Number(expectedPriceRange?.max) > 0
    ? (Number(MOQ) * Number(expectedPriceRange.max)).toLocaleString("en-IN")
    : null;

  const minPrice = Number(expectedPriceRange?.min || 0);
  const maxPrice = Number(expectedPriceRange?.max || 0);

  const minimumAllowedMaxPrice =
    minPrice > 0 ? Number((minPrice * 1.1).toFixed(2)) : 0;

  return (
    <div className="space-y-5">

      {/* ── MOQ ─────────────────────────────────────────────────────────── */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700">
            Required Quantity <span className="text-primaryColor">*</span>
          </label>
          {varietyMOQ && (
            <span className="text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full">
              Min. order: {varietyMOQ.toLocaleString("en-IN")} {standardizedUnit}
            </span>
          )}
        </div>

        {/* MOQ input with KG badge */}
        <div className="relative">
          <label className="absolute -top-2 left-3 text-[10px] font-medium text-primaryColor bg-white px-1">
            Required Quantity*
          </label>
          <input
            type="number"
            min={varietyMOQ || 1}
            value={MOQ}
            onWheel={(e) => e.target.blur()}
            onChange={(e) => onChange("MOQ", e.target.value)}
            placeholder={`Min ${varietyMOQ || 1} ${standardizedUnit}`}
            className="w-full pl-4 pr-16 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-primaryColor focus:ring-1 focus:ring-primaryColor/20 transition-all"
          />
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
            {standardizedUnit}
          </span>
        </div>

        {/* Quick quantity pills */}
        <div className="flex gap-2 flex-wrap">
          {QUICK_MOQ.filter(q => !varietyMOQ || q >= varietyMOQ).map((q) => (
            <button
              key={q}
              type="button"
              onClick={() => onChange("MOQ", String(q))}
              className={`
                px-3 py-1 rounded-md text-xs font-medium border transition-all duration-150 active:scale-95
                ${MOQ === String(q)
                  ? "bg-primaryColor text-white border-primaryColor"
                  : "bg-white text-gray-600 border-gray-300 hover:border-primaryColor hover:text-primaryColor"
                }
              `}
            >
              {q} {standardizedUnit}
            </button>
          ))}
        </div>

        {/* Validation hint */}
        {varietyMOQ && MOQ && Number(MOQ) < varietyMOQ && (
          <p className="text-xs text-red-500 flex items-center gap-1">
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Quantity must be at least {varietyMOQ.toLocaleString("en-IN")} {standardizedUnit} (minimum order quantity)
          </p>
        )}
      </div>

      {/* ── Expected Price Range ─────────────────────────────────────────── */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700">
            Expected Price Range <span className="text-xs text-gray-400">(Without GST)</span>
          </label>
          <div className="flex gap-1.5">
            {["INR", "USD"].map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => onChange("currency", c)}
                className={`
                  px-2.5 py-0.5 rounded-md text-xs font-bold border transition-all duration-150
                  ${currency === c
                    ? "bg-primaryColor text-white border-primaryColor"
                    : "bg-white text-gray-500 border-gray-300 hover:border-primaryColor"
                  }
                `}
              >
                {c === "INR" ? "₹ INR" : "$ USD"}
              </button>
            ))}
          </div>
        </div>

        {/* {floorPrice && capPrice && (
          <p className="text-[11px] text-gray-500">
            Allowed range: ₹{floorPrice} – ₹{capPrice} per Kg
          </p>
        )} */}

        <div className="grid grid-cols-2 gap-3">
          <div className="relative">
            <label className="absolute -top-2 left-3 text-[10px] font-medium text-primaryColor bg-white px-1">
              Min Per {standardizedUnit}*
            </label>
            <input
              type="number"
              min={0}
              value={expectedPriceRange?.min || ""}
              onWheel={(e) => e.target.blur()}
              onChange={(e) => onChange("priceMin", e.target.value)}
              placeholder="0"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-primaryColor focus:ring-1 focus:ring-primaryColor/20 transition-all"
            />
          </div>
          <div className="relative">
            <label className="absolute -top-2 left-3 text-[10px] font-medium text-primaryColor bg-white px-1">
              Max Per {standardizedUnit}*
            </label>
            <input
              type="number"
              min={0}
              value={expectedPriceRange?.max || ""}
              onWheel={(e) => e.target.blur()}
              onChange={(e) => onChange("priceMax", e.target.value)}
              placeholder="0"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-primaryColor focus:ring-1 focus:ring-primaryColor/20 transition-all"
            />
          </div>
        </div>

        {Boolean(floorPrice) && Boolean(expectedPriceRange?.min) && Number(expectedPriceRange.min) < Number(floorPrice) && (
          <p className="text-xs text-red-500">
            Min price cannot be less than ₹{floorPrice}
          </p>
        )}

        {Boolean(capPrice) && Boolean(expectedPriceRange?.max) && Number(expectedPriceRange.max) > Number(capPrice) && (
          <p className="text-xs text-red-500">
            Max price cannot exceed ₹{capPrice}
          </p>
        )}

        {Boolean(Number(expectedPriceRange?.min)) &&
          Boolean(Number(expectedPriceRange?.max)) &&
          maxPrice < minimumAllowedMaxPrice && (
            <p className="text-xs text-red-500">
              Max price must be at least 10% higher than the minimum price
              (minimum: ₹{Math.round(minimumAllowedMaxPrice)})
            </p>
          )}

        {Boolean(totalMin) && Boolean(totalMax) && (
          <div className="flex items-center justify-between bg-primaryColor/5 border border-primaryColor/15 rounded-lg px-4 py-2.5">
            <span className="text-xs text-gray-500">Total Quotation Range —</span>
            <span className="text-sm font-bold text-primaryColor">
              {currency === "INR" ? "₹" : "$"}{totalMin} – {currency === "INR" ? "₹" : "$"}{totalMax}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default PriceAndQuantity;